import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Copyright, MessageCircle, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { trackFooterContactClick } from "@/lib/ga4";

const Footer = () => {
  const { t } = useTranslation();

  return (
    <footer className="bg-card mt-auto" role="contentinfo">
      <div className="container mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Menu */}
          <div>
            <h3 className="font-heading text-xl font-semibold mb-4 text-primary">{t("footer.menu")}</h3>
            <nav className="flex flex-col space-y-2" aria-label="Footer navigation">
              <Link to="/" className="text-foreground/80 hover:text-primary transition-colors">
                {t("nav.home")}
              </Link>
              <Link to="/about" className="text-foreground/80 hover:text-primary transition-colors">
                {t("nav.about")}
              </Link>
              <Link to="/services" className="text-foreground/80 hover:text-primary transition-colors">
                {t("nav.services")}
              </Link>
              <Link to="/portfolio" className="text-foreground/80 hover:text-primary transition-colors">
                {t("nav.portfolio")}
              </Link>
              <Link to="/faq" className="text-foreground/80 hover:text-primary transition-colors">
                {t("nav.faq")}
              </Link>
              <Link to="/contact" className="text-foreground/80 hover:text-primary transition-colors">
                {t("nav.contact")}
              </Link>
            </nav>
          </div>

          {/* Leistungen */}
          <div>
            <h3 className="font-heading text-xl font-semibold mb-4 text-primary">{t("footer.services.title")}</h3>
            <ul className="flex flex-col space-y-2" role="list">
              <li className="text-foreground/80">{t("footer.services.hotel")}</li>
              <li className="text-foreground/80">{t("footer.services.video")}</li>
              <li className="text-foreground/80">{t("footer.services.interior")}</li>
              <li className="text-foreground/80">{t("footer.services.food")}</li>
              <li className="text-foreground/80">{t("footer.services.aerial")}</li>
            </ul>
          </div>

          {/* Contact Details */}
          <div>
            <h3 className="font-heading text-xl font-semibold mb-4 text-primary">{t("footer.contact.title")}</h3>
            <div className="flex flex-col space-y-3">
              <a 
                href="tel:+491716501432" 
                className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors"
                aria-label={t("footer.contact.call")}
                onClick={() => trackFooterContactClick({ contact_type: 'phone' })}
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                {t("footer.contact.call")}
                <ExternalLink className="w-3 h-3 ml-auto" aria-hidden="true" />
              </a>
              <a 
                href="https://wa.me/491716501432" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors"
                aria-label={t("footer.contact.whatsapp")}
                onClick={() => trackFooterContactClick({ contact_type: 'whatsapp' })}
              >
                <MessageCircle className="w-4 h-4" aria-hidden="true" />
                {t("footer.contact.whatsapp")}
                <ExternalLink className="w-3 h-3 ml-auto" aria-hidden="true" />
              </a>
              <a 
                href="mailto:info@leuchtende-hotelfotografie.de?subject=Anfrage%20von%20der%20Webseite&body=Lieber%20Herr%20Haberland%2C%0A%0A" 
                className="flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors"
                aria-label={t("footer.contact.email")}
                onClick={() => trackFooterContactClick({ contact_type: 'email' })}
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                {t("footer.contact.email")}
                <ExternalLink className="w-3 h-3 ml-auto" aria-hidden="true" />
              </a>
              <div className="flex items-start gap-2 text-foreground/80">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" aria-hidden="true" />
                <span>
                  München | Berlin | Cape Town
                </span>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div>
            <h3 className="font-heading text-xl font-semibold mb-4 text-primary">{t("footer.about.title")}</h3>
            <p className="text-foreground/80 text-sm">
              {t("footer.about.description")}
            </p>
          </div>
        </div>

        {/* SEO Portfolio Links */}
        <div className="pt-8 border-t border-border">
          <h3 className="font-heading text-xl font-semibold mb-4 text-primary">
            {t("footer.portfolio.title")}
          </h3>
          <nav className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm" aria-label="Portfolio categories">
            {/* Categories */}
            <Link to="/portfolio?category=Hotel Photos" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.hotelPhotos")}
            </Link>
            <Link to="/portfolio?category=Hotel Videos" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.hotelVideos")}
            </Link>
            <Link to="/portfolio?category=Food Photos" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.foodPhotography")}
            </Link>
            <Link to="/portfolio?category=Aerial Photos" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.hotelAerialPhotos")}
            </Link>
            <Link to="/portfolio?category=Aerial Videos" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.hotelAerialVideos")}
            </Link>
            
            {/* Stars */}
            <Link to="/portfolio?stars=5" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.fiveStarHotels")}
            </Link>
            <Link to="/portfolio?stars=4" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.fourStarHotels")}
            </Link>
            
            {/* Types */}
            <Link to="/portfolio?type=Business" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.businessHotelPhotos")}
            </Link>
            <Link to="/portfolio?type=Wellness" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.wellnessHotelPhotography")}
            </Link>
            <Link to="/portfolio?type=Vacation" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.vacationHotelPortfolio")}
            </Link>
            <Link to="/portfolio?type=Lifestyle" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.lifestyleHotelImages")}
            </Link>
            
            {/* Styles */}
            <Link to="/portfolio?style=Hotel" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.hotelPhotography")}
            </Link>
            <Link to="/portfolio?style=Boutique" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.boutiqueHotelPortfolio")}
            </Link>
            <Link to="/portfolio?style=Resort" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.resortPhotography")}
            </Link>
            
            {/* Combined filters */}
            <Link to="/portfolio?stars=5&category=Hotel Photos" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.fiveStarHotelPhotos")}
            </Link>
            <Link to="/portfolio?category=Hotel Videos&stars=5" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.luxuryHotelVideos")}
            </Link>
            <Link to="/portfolio?category=Food Photos&type=Wellness" className="text-foreground/80 hover:text-primary transition-colors">
              {t("footerLinks.wellnessFoodPhotography")}
            </Link>
          </nav>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border text-foreground/70 text-sm">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center justify-center gap-2 flex-wrap flex-1">
              <span className="flex items-center gap-1">
                <Copyright className="w-4 h-4" />
                2025
              </span>
              <span>|</span>
              <span>Thomas Haberland</span>
              <span>|</span>
              <span className="flex items-center gap-1">
                {t("footer.copyright")} <span className="text-red-500">❤️</span> {t("footer.from")}{" "}
                <a 
                  href="https://rainmaker.travel" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  rainmaker.travel
                  <ExternalLink className="w-3 h-3" />
                </a>
              </span>
            </div>
            <div className="flex items-center gap-2" role="group" aria-label="Legal links">
              <Link to="/impressum" className="hover:text-primary transition-colors">
                {t("footer.legal.impressum")}
              </Link>
              <span>|</span>
              <Link to="/datenschutz" className="hover:text-primary transition-colors">
                {t("footer.legal.datenschutz")}
              </Link>
              <span>|</span>
              <a href="/llms.txt" className="hover:text-primary transition-colors" aria-label="LLMs.txt file for AI platforms">
                llms.txt
              </a>
              <span>|</span>
              <a href="/llms-full.txt" className="hover:text-primary transition-colors" aria-label="Full LLMs information file">
                llms-full.txt
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;